import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import MaterialRadio, { RadioProps } from "@material-ui/core/Radio";
import MaterialRadioGroup from "@material-ui/core/RadioGroup";
import { makeStyles } from "@material-ui/styles";
import clsx from "clsx";
import React, { ReactNode } from "react";
import { colours } from "../../styles/colours";

const size = 16;
const ratioOfBead = 0.875;

const useRadioStyles = makeStyles({
  root: {
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  icon: {
    borderRadius: "50%",
    width: size,
    height: size,
    boxShadow:
      "inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)",
    backgroundColor: colours.white,
    backgroundImage:
      "linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))",
    "$root.Mui-focusVisible &": {
      outline: `3px ${colours.primaryBlue}`,
      outlineOffset: 2,
    },
    "input:hover ~ &": {
      backgroundColor: "#ebf1f5",
    },
    "input:disabled ~ &": {
      boxShadow: "none",
      background: "rgba(206,217,224,.5)",
    },
  },
  checkedIcon: {
    backgroundColor: colours.primaryBlue,
    backgroundImage:
      "linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))",
    "&:before": {
      display: "block",
      width: ratioOfBead * size,
      height: ratioOfBead * size,
      backgroundImage: "radial-gradient(#fff,#fff 28%,transparent 32%)",
      content: '""',
      position: "relative",
      top: (size - ratioOfBead * size) / 2,
      left: (size - ratioOfBead * size) / 2,
    },
    "input:hover ~ &": {
      backgroundColor: colours.primaryBlue,
    },
  },
});

export const useRadioGroupStyles = makeStyles({
  root: {
    width: "100%",
  },
  wrapper: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  },
});

// Inspired by blueprintjs
export function StyledRadio(props: RadioProps) {
  const classes = useRadioStyles();
  return (
    <MaterialRadio
      className={classes.root}
      disableRipple
      color="default"
      checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
      icon={<span className={classes.icon} />}
      {...props}
    />
  );
}

type RadioGroupProps<T> = {
  title?: string;
  options: Array<{
    id: T;
    label: ReactNode;
  }>;
  value: T;
  onChange: (t: string) => any;
  fullWidth?: boolean;
  inline?: boolean;
};

export function RadioGroup<T extends string | number>({
  title,
  options,
  value,
  onChange,
  fullWidth,
  inline
}: RadioGroupProps<T>) {
  const styles = useRadioGroupStyles();
  return (
    <FormControl component="fieldset" className={fullWidth ? styles.root : ""}>
      {title ? <FormLabel component="legend">{title}</FormLabel> : null}
      <MaterialRadioGroup
        defaultValue={value ?? ''}
        aria-label={title}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className={inline ? styles.wrapper : ""}
      >
        {options.map(({ id, label }) => (
          <FormControlLabel
            key={id}
            value={id}
            control={<StyledRadio />}
            label={label}
          />
        ))}
      </MaterialRadioGroup>
    </FormControl>
  );
}
